import React from 'react';
import { Link } from '@/components/LocalizedLink';
import { ArrowRight, Shield, Leaf, FlaskConical, FileCheck, Truck, Globe, Handshake, BarChart3 } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const fadeLeft = { hidden: { opacity: 0, x: -40 }, visible: { opacity: 1, x: 0 } };
const fadeRight = { hidden: { opacity: 0, x: 40 }, visible: { opacity: 1, x: 0 } };

export default function WhyChoosePage() {
  const features = [
    { icon: Shield, title: 'Quality Assurance', desc: 'Every batch undergoes rigorous quality checks. We test for moisture, foreign matter, color value, and microbiological parameters as per international standards.', color: '#D4870A' },
    { icon: Leaf, title: 'Farm-Direct Sourcing', desc: 'We procure directly from farming communities in Maharashtra, Andhra Pradesh, Rajasthan, and Gujarat. This ensures freshness, eliminates middlemen, and guarantees traceability.', color: '#6B8E23' },
    { icon: FlaskConical, title: 'Lab Testing', desc: 'Independent third-party lab testing for every consignment. Full analysis reports for pesticide residues, aflatoxin levels, heavy metals, and adulterants.', color: '#C0392B' },
    { icon: FileCheck, title: 'Export Compliance', desc: 'All documentation ready: FSSAI certificates, phytosanitary certificates, certificate of origin, fumigation certificates, and custom invoices.', color: '#E8A020' },
    { icon: Truck, title: 'Reliable Logistics', desc: 'End-to-end shipping management. FCL and LCL options. FOB/CIF/CNF pricing available. We work with trusted freight forwarders for timely delivery.', color: '#6B3A1F' },
    { icon: Globe, title: 'Global Reach', desc: 'Active exports to 15+ countries. Deep understanding of import regulations for UAE, UK, EU, and Asian markets. We handle all compliance requirements.', color: '#D4870A' },
    { icon: Handshake, title: 'Buyer Partnerships', desc: 'We believe in long-term relationships. Dedicated account management, regular market updates, and priority allocation during peak seasons.', color: '#8B4E2A' },
    { icon: BarChart3, title: 'Competitive Pricing', desc: 'Direct farm procurement + efficient processing = best-in-class FOB pricing. We offer transparent cost breakdowns so you can make informed decisions.', color: '#E8A020' },
  ];

  return (
    <div data-testid="why-choose-page">
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-saffron-50/50 to-cream" />
        <div className="relative max-w-7xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.7 }}>
            <span className="text-xs uppercase tracking-[0.3em] text-saffron font-sans font-semibold">Why Choose Us</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-earth-dark mt-3 tracking-tight">
              Why Buyers Trust<br /><span className="text-saffron">ASJK Laxmi Global</span>
            </h1>
            <p className="mt-4 text-lg text-earth/60 font-sans max-w-xl leading-relaxed">
              From farm to port, we control the entire supply chain to deliver consistent quality, competitive pricing, and reliable service.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={i % 2 === 0 ? fadeLeft : fadeRight}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              className="bg-white p-7 rounded-lg border border-saffron-100/50 shadow-card-lift hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-300 group"
              data-testid={`feature-${i}`}
            >
              <div className="flex items-start gap-5">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: feature.color + '15' }}>
                  <feature.icon size={22} style={{ color: feature.color }} />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-earth-dark group-hover:text-saffron transition-colors">{feature.title}</h3>
                  <p className="mt-2 text-sm text-earth/60 font-sans leading-relaxed">{feature.desc}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-saffron-50/30" data-testid="process-section">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron font-sans font-semibold">Our Process</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-earth-dark mt-3 tracking-tight">From Farm to Your Warehouse</h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { step: '01', title: 'Farm Sourcing', desc: 'Direct procurement from verified farming communities' },
              { step: '02', title: 'Quality Check', desc: 'Multi-stage inspection & grading at our facility' },
              { step: '03', title: 'Lab Testing', desc: 'Third-party lab analysis for purity & safety' },
              { step: '04', title: 'Processing', desc: 'Cleaning, grading, packaging per buyer specs' },
              { step: '05', title: 'Export', desc: 'Documentation, logistics, and delivery to port' },
            ].map((item, i) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, duration: 0.5 }}
                className="text-center bg-white p-6 rounded-lg border border-saffron-100/50 shadow-card-lift hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 relative group"
                data-testid={`step-${item.step}`}
              >
                <span className="font-display text-4xl font-bold text-saffron/15 group-hover:text-saffron/30 transition-colors">{item.step}</span>
                <h3 className="font-display text-base font-bold text-earth-dark mt-2">{item.title}</h3>
                <p className="mt-2 text-xs text-earth/50 font-sans">{item.desc}</p>
                {i < 4 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-saffron/20" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-spice-deep text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">Experience the Laxmi Global Difference</h2>
          <p className="mt-2 text-cream-muted/60 font-sans">Request a sample or get a competitive quote for your next order.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-saffron text-white font-sans font-semibold text-sm px-8 py-3.5 rounded-md hover:bg-saffron-dark transition-colors" data-testid="why-cta">
              Request Quote <ArrowRight size={16} />
            </Link>
            <Link to="/products" className="inline-flex items-center gap-2 border border-cream-muted/30 text-cream-warm font-sans font-semibold text-sm px-8 py-3.5 rounded-md hover:bg-cream-warm/5 transition-colors">
              View Products
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
